"use client";

import React, { forwardRef } from "react";
import Link from "next/link";
import { clampLegalReadingProgress } from "@/src/features/legal/legalReadingProgress";

// Keep legal pages buildable when a hosted font revision is retired upstream.
const fraunces = { className: "font-serif" } as const;

export type LegalPageVariant =
  | "privacy"
  | "terms"
  | "security"
  | "disaster-recovery"
  | "subprocessors"
  | "cookies"
  | "updates"
  | "changelog"
  | "status"
  | "help";

const ACCENT: Record<LegalPageVariant, string> = {
  privacy: "0, 212, 255",
  terms: "244, 196, 48",
  security: "110, 231, 183",
  "disaster-recovery": "251, 146, 60",
  subprocessors: "165, 180, 252",
  cookies: "244, 114, 182",
  updates: "148, 163, 184",
  changelog: "196, 181, 253",
  status: "125, 211, 252",
  help: "94, 234, 212",
};

const FOOTER_LINKS: { href: string; label: string; key: LegalPageVariant }[] = [
  { href: "/security", label: "Security", key: "security" },
  { href: "/cookies", label: "Cookies", key: "cookies" },
  { href: "/subprocessors", label: "Subprocessors", key: "subprocessors" },
  { href: "/legal-updates", label: "Legal updates", key: "updates" },
  { href: "/help", label: "Help", key: "help" },
  { href: "/status", label: "Status", key: "status" },
  { href: "/changelog", label: "Changelog", key: "changelog" },
];

function LegalNav({ current }: { current: LegalPageVariant }) {
  const items = [
    { href: "/security", label: "Security", key: "security" as const },
    { href: "/privacy-policy", label: "Privacy", key: "privacy" as const },
    { href: "/terms", label: "Terms", key: "terms" as const },
  ];

  return (
    <nav data-legal-chrome="nav" className="relative z-10 mx-auto flex max-w-[960px] items-center justify-between px-6 pt-8 font-sans md:px-8">
      <Link
        href="/"
        className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 text-[13px] font-medium tracking-wide text-white/60 transition-colors duration-300 hover:text-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white motion-reduce:transition-none"
      >
        Smart Manager
      </Link>
      <div className="flex items-center gap-3 sm:gap-6">
        {items.map(({ href, label, key }) => (
          <Link
            key={href}
            href={href}
            aria-current={current === key ? "page" : undefined}
            className={`inline-flex min-h-11 min-w-11 items-center justify-center px-1 text-[13px] font-medium transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white motion-reduce:transition-none ${
              current === key
                ? "text-white/90"
                : "text-white/50 hover:text-white/75"
            }`}
          >
            {label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

/**
 * The page's title block. Each part carries `data-legal-chrome`, the stable
 * hook the native app's legal reader hides (its own header names the page);
 * the lead and the date stay. Phones get a short lead-in, so the title sits
 * under the nav instead of a screen-height gap below it.
 */
export function LegalHero({ eyebrow, title, lead, meta }: {
  eyebrow: string;
  title: string;
  lead?: string;
  meta: React.ReactNode;
}) {
  return (
    <header className="mx-auto max-w-[960px] px-6 pb-6 pt-8 md:px-8 md:pt-28">
      <p
        data-legal-chrome="eyebrow"
        className="mb-3 text-[13px] font-medium tracking-wide text-white/50 md:mb-5"
        style={{ animation: "legalFadeIn 0.6s ease-out 0.05s both" }}
      >
        {eyebrow}
      </p>
      <h1
        data-legal-chrome="title"
        className={`${fraunces.className} text-[2.6rem] font-light leading-[1.1] tracking-[-0.02em] text-white/90 md:text-[3.8rem] lg:text-[4.6rem]`}
        style={{ animation: "legalFadeIn 0.7s ease-out 0.1s both" }}
      >
        {title}
      </h1>
      {lead ? (
        <p
          className="mt-4 max-w-[62ch] text-[16px] leading-[1.75] text-white/70 md:mt-6 md:text-[17px]"
          style={{ animation: "legalFadeIn 0.6s ease-out 0.16s both" }}
        >
          {lead}
        </p>
      ) : null}
      <p
        className="mt-4 text-[14px] text-white/55 md:mt-6"
        style={{ animation: "legalFadeIn 0.6s ease-out 0.2s both" }}
      >
        {meta}
      </p>
      <div
        data-legal-chrome="divider"
        className="mt-8 h-px bg-white/[0.06] md:mt-12"
        style={{ animation: "legalFadeIn 0.5s ease-out 0.3s both" }}
      />
    </header>
  );
}

type LegalPageShellProps = {
  variant: LegalPageVariant;
  progress: number;
  hero: React.ReactNode;
  children: React.ReactNode;
};

export const LegalPageShell = forwardRef<HTMLDivElement, LegalPageShellProps>(
  function LegalPageShell({ variant, progress, hero, children }, ref) {
    const rgb = ACCENT[variant];
    const safeProgress = clampLegalReadingProgress(progress);

    return (
      <div
        ref={ref}
        lang="en"
        dir="ltr"
        className="legal-scroll h-screen w-full overflow-y-auto scroll-smooth font-sans motion-reduce:scroll-auto"
        style={{ background: "#09090b" }}
      >
        {/* Progress */}
        <div
          role="progressbar"
          aria-label="Reading progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(safeProgress)}
          className="fixed left-0 right-0 top-0 z-50 h-px bg-white/[0.04]"
        >
          <div
            className="h-full transition-[width] duration-200 ease-out motion-reduce:transition-none"
            style={{
              width: `${safeProgress}%`,
              background: `rgba(${rgb}, 0.7)`,
              boxShadow: `0 0 12px rgba(${rgb}, 0.3)`,
            }}
          />
        </div>

        {/* One subtle glow -- that's it */}
        <div className="pointer-events-none fixed inset-0">
          <div
            className="absolute -top-[30%] left-1/2 h-[800px] w-[1200px] -translate-x-1/2 rounded-full opacity-100 blur-[160px]"
            style={{ background: `rgba(${rgb}, 0.03)` }}
          />
        </div>

        <LegalNav current={variant} />

        <div className="relative z-10">
          {hero}
          {children}
        </div>

        {/* Footer */}
        <footer data-legal-chrome="footer" className="relative z-10 mx-auto max-w-[960px] px-6 pb-16 pt-4 font-sans md:px-8">
          <div className="mb-8 h-px bg-white/[0.06]" />
          <div className="flex flex-col justify-between gap-4 text-[13px] text-white/50 sm:flex-row sm:items-center">
            <span>© {new Date().getFullYear()} SmartManager LLC</span>
            <div className="-ml-1 flex flex-wrap items-center gap-x-4 gap-y-1 sm:ml-0 sm:justify-end">
              <Link
                href="/privacy-policy"
                aria-current={variant === "privacy" ? "page" : undefined}
                className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 transition-colors hover:text-white/75"
              >
                Privacy
              </Link>
              <Link
                href="/terms"
                aria-current={variant === "terms" ? "page" : undefined}
                className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 transition-colors hover:text-white/75"
              >
                Terms
              </Link>
              {FOOTER_LINKS.map(({ href, label, key }) => (
                <Link
                  key={href}
                  href={href}
                  aria-current={variant === key ? "page" : undefined}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 transition-colors hover:text-white/75"
                >
                  {label}
                </Link>
              ))}
              <a href="mailto:jonathan@smartmanager.ai" className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 transition-colors hover:text-white/75">Contact</a>
            </div>
          </div>
        </footer>

        <style>{`
          @keyframes legalFadeIn {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @media (prefers-reduced-motion: reduce) {
            .legal-scroll * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
          }
          .legal-scroll::-webkit-scrollbar { width: 4px; }
          .legal-scroll::-webkit-scrollbar-track { background: transparent; }
          .legal-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.06); border-radius: 99px; }
          .legal-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.12); }
          .legal-toc::-webkit-scrollbar { width: 2px; }
          .legal-toc::-webkit-scrollbar-track { background: transparent; }
          .legal-toc::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 99px; }
        `}</style>
      </div>
    );
  }
);

export { fraunces, ACCENT };
